import { useEffect, useState, type JSX } from 'react';
import classNames from 'classnames';
import { Card, CardProps } from '../../Atoms/Card';
import { CollapseButton } from '../../Atoms/CollapseButton';
import { Text, TextColors } from '../../Atoms/Text';
import { Icon, IconType } from '../../Atoms/Icon';

export interface CardWithTitleProps extends CardProps {
  title: string;
  titleIcon?: IconType;
  enableCollapse?: boolean;
  titlePadding?: string;
  titleColor?: keyof typeof TextColors;
  headerContent?: JSX.Element | JSX.Element[];
  childrenWrapperClass?: string;
  cardActions?: JSX.Element | JSX.Element[];
  className?: string;
  collapsed: boolean;
  dataCy?: string;
  dataTestId?: string;
  onCollapseClick?: (collapse: boolean) => void;
}

export function CardWithTitle({
  title,
  enableCollapse = false,
  onCollapseClick,
  titlePadding = 'p-5',
  titleColor,
  headerContent,
  children,
  className,
  childrenWrapperClass = '',
  titleIcon,
  cardActions,
  collapsed,
  dataCy,
  dataTestId,
  ...props
}: CardWithTitleProps) {
  const [collapse, setCollapse] = useState(collapsed);

  useEffect(() => {
    setCollapse(collapsed);
  }, [collapsed]);

  return (
    <Card
      {...props}
      data-cy={dataCy || ''}
      data-testid={dataTestId || ''}
      className={classNames('', { [className as string]: true })}>
      <div className="flex">
        <div className={`flex items-center ${titlePadding} `}>
          {titleIcon && (
            <Icon
              className="mr-3"
              type={titleIcon}
            />
          )}
          <Text
            className="font-semibold"
            color={titleColor || 'grey9'}
            size="sm">
            {title}
          </Text>
          {headerContent}
        </div>
        <div className="ml-auto self-center px-6">{cardActions}</div>
        {enableCollapse && (
          <CollapseButton
            classNames="border-l px-6"
            collapse={collapse}
            setCollapse={(state) => {
              setCollapse(state);
              if (onCollapseClick) onCollapseClick(state as boolean);
            }}
          />
        )}
      </div>
      <>{!collapse && <div className={classNames('border-t', { [childrenWrapperClass]: true })}>{children}</div>}</>
    </Card>
  );
}
